import api from '@api'
import { generateId } from '@/src/utils/id'
import { isPortalLogin } from '@utils/auth'

const CSV_TYPE_MAP = {
  user: 'UserCsvWizardContext',
  asset: 'AssetCsvWizardContext',
  request: 'RequestCsvWizardContext',
  service_catalog: 'RequestCsvWizardContext',
  department: 'DepartmentCSVWizardContext',
  location: 'LocationCSVWizardContext',
  contract: 'ContractCsvWizardContext',
  vendor: 'VendorCSVWizardContext',
  product: 'ProductCSVWizardContext',
  manufacturer: 'ManufacturerCSVWizardContext',
  productvendor: 'ProductVendorCsvWizardContext',
  category: 'CategoryCsvWizardContext',
  dependent_value: 'DependentValueCsvWizardContext',
  single_column: 'SingleColumnCsvImportWizardContext',
  ip_network: 'IpNetworkCsvWizardContext',
  cloud_network: 'CloudNetworkCsvWizardContext',
  private_cloud_network: 'PrivateCloudNetworkCsvWizardContext',
  wmi_credential: 'CredentialCsvWizardContext',
  ssh_credential: 'CredentialCsvWizardContext',
  snmp_credential: 'CredentialCsvWizardContext',
  vmware_credential: 'CredentialCsvWizardContext',
  aws_credential: 'CredentialCsvWizardContext',
  azure_credential: 'CredentialCsvWizardContext',
  nutanix_credential: 'CredentialCsvWizardContext',
  cmdb: 'AssetCsvWizardContext',
  asset_hardware: 'AssetCsvWizardContext',
  asset_software: 'AssetCsvWizardContext',
  asset_non_it: 'AssetCsvWizardContext',
  asset_consumable: 'AssetCsvWizardContext',
  software_license: 'SoftwareLicenseWizardContext',
  snmp_property_device: 'SnmpPropertyDeviceWizardContext',
  snmp_property: 'SnmpPropertyWizardContext',
  project: 'ProjectImportWizardContext',
  task: 'TaskCsvWizardContext',
}

export function transformCsvContext(context, stage) {
  const wizardContext = context.wizardContext
  if (wizardContext) {
    return {
      error: (context.messages || []).length ? context.messages : null,
      ...(stage === 'save_batch'
        ? {
            success: context.successMessages
              ? context.successMessages[0]
              : null,
          }
        : {}),
      importFileType: wizardContext.importFileType,
      dateFormat: wizardContext.dateFormat,
      discoveryType: wizardContext.discoveryType,
      ipRangeType: wizardContext.ipRangeType,
      cloudType: wizardContext.cloudType,
      privateCloudType: wizardContext.privateCloudType,
      snmpFieldType: wizardContext.fieldType,
      file: wizardContext.csvFile,
      headers: wizardContext.header,
      columnMappings: wizardContext.columnMapping,
      selectedRows: [],
      table: ((wizardContext.table || {}).rowList || []).map((r) => ({
        ...r.cellValues,
        guid: generateId(),
      })),
      ...(wizardContext.assetCiTypeId
        ? { assetCiType: wizardContext.assetCiTypeId }
        : {}),
      ...(wizardContext.serviceCatalogId
        ? { serviceCatalogId: wizardContext.serviceCatalogId }
        : {}),
      tree: wizardContext.tree,
      options: wizardContext.options,
    }
  } else {
    return {
      batchId: context.batchId,
      batchNumber: context.batchNumber,
      error: (context.messages || []).length ? context.messages : null,
      ...(stage === 'save_batch'
        ? {
            success: context.successMessages
              ? context.successMessages[0]
              : null,
          }
        : {}),
      wizardContext: wizardContext,
    }
  }
}

export function transformCsvContextForServer(data, selectedRows) {
  if (!CSV_TYPE_MAP[data.moduleName]) {
    throw new Error(`type for ${data.moduleName} not found in csv wizard`)
  }
  let table
  if (selectedRows && selectedRows.length) {
    table = {
      rowList: data.table
        .filter((r) => selectedRows.indexOf(r.guid) >= 0)
        .map((r) => ({ cellValues: r })),
    }
  }
  return {
    type: CSV_TYPE_MAP[data.moduleName],
    dateFormat: data.dateFormat,
    importFileType: data.importFileType,
    ...(data.file ? { csvFile: data.file } : {}),
    ...(data.assetCiType ? { assetCiTypeId: data.assetCiType } : {}),
    ...(data.discoveryType ? { discoveryType: data.discoveryType } : {}),
    ...(data.ipRangeType ? { ipRangeType: data.ipRangeType } : {}),
    ...(data.cloudType ? { cloudType: data.cloudType } : {}),
    ...(data.privateCloudType
      ? { privateCloudType: data.privateCloudType }
      : {}),
    ...(data.snmpFieldType ? { fieldType: data.snmpFieldType } : {}),
    ...(data.fieldType ? { fieldType: data.fieldType } : {}),
    ...(data.userType ? { userType: data.userType } : {}),
    ...(data.refModel ? { refModel: data.refModel } : {}),
    ...(data.fieldId ? { fieldId: data.fieldId } : {}),
    ...(data.headers ? { header: data.headers } : {}),
    ...(data.columnMappings ? { columnMapping: data.columnMappings } : {}),
    ...(data.serviceCatalogId
      ? { serviceCatalogId: data.serviceCatalogId }
      : {}),
    ...(data.projectId ? { projectId: data.projectId } : {}),
    ...(table ? { table } : {}),
  }
}

export function getSupportDataApi(moduleName, additionalUrlParams = {}) {
  return api
    .get(`${isPortalLogin() ? '/cportal' : ''}/csvwizard/${moduleName}`, {
      params: {
        ...additionalUrlParams,
      },
      notify: true,
    })
    .then((data) => {
      return data.propertySet.map((p) => ({
        columnName: p.displayName,
        columnKey: p.propKey,
        required: p.required,
        groupName: p.groupName,
      }))
    })
}

export function processStageApi(moduleName, stage, data, selectedRows) {
  return api
    .post(
      `${isPortalLogin() ? '/cportal' : ''}/csvwizard/${moduleName}/${stage}`,
      transformCsvContextForServer({ ...data, moduleName }, selectedRows),
      {
        notify: true,
      }
    )
    .then((data) => transformCsvContext(data, stage))
}
